import { useMemo } from "react";
import { useUserCountry } from "@src/packages/location/useUserCountry";
import { useDetectedCountry } from "@src/packages/location/useDetectedCountry";
import { useActiveCycle } from "@src/packages/cycles/hooks/useCycles";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import {
  resolveEmergencySections,
  type EmergencySection,
} from "../emergencyNumbers";

/**
 * Who she should call, resolved before she needs it.
 *
 * All of it comes from production data — her cycle's care team for the clinic,
 * where she seems to be (the same guess sign-in makes: IP on web, region
 * setting on a phone) and her profile country for the emergency services.
 *
 * Called from the top of `InjectionCoachWalk`, not from the paused card, so the
 * queries start when the coach opens and have the whole walk to land. Asking at
 * the moment of the pause would be asking at the worst moment: she would watch a
 * fallback button turn into a phone number while being told to get emergency
 * help.
 *
 * Every field can be empty and the caller must handle that. A patient with no
 * active cycle, a care team with no phone on file, or countries with no listed
 * numbers all produce nothing, and the escalation card falls back to the
 * care-team screen so it is never a dead end.
 */

export type CoachClinicContact = {
  /** The clinic's name where the cycle records one — used to address her. */
  name: string | null;
  /** As recorded, spaces and all. Dial with `telHref`. */
  phone: string;
};

export type CoachEscalationContacts = {
  /** Where she seems to be first, then the profile country when it differs. */
  emergency: EmergencySection[];
  clinic: CoachClinicContact | null;
};

export function useCoachEscalationContacts(): CoachEscalationContacts {
  const { countryCode: profileCountry } = useUserCountry();
  // No saved country here on purpose: this asks where she seems to be, and
  // the profile is the other section. A fallback is not a place, so the
  // section is left out.
  const here = useDetectedCountry();
  const hereCountry = here.source === "fallback" ? null : here.countryCode;
  const hereBasis = here.source === "ip" ? "ip" : "device";
  const { data: activeCycle } = useActiveCycle();
  const { data: careTeam } = useCareTeam(activeCycle?.id);

  const emergency = useMemo(
    () => resolveEmergencySections({ hereCountry, hereBasis, profileCountry }),
    [hereBasis, hereCountry, profileCountry],
  );

  // The first member who left a number. The care team is ordered by the API and
  // a cycle usually has one reachable line; picking the first with a phone beats
  // showing her a list to choose from at the one moment choosing is hardest.
  const phone = careTeam
    ?.map((member) => member.phoneNumber?.trim())
    .find((value): value is string => Boolean(value));

  return {
    emergency,
    clinic: phone ? { name: activeCycle?.provider ?? null, phone } : null,
  };
}
